import React, { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import "../styles/components/played-match-view.scss";
import { baseURL } from "../Constants";
import { AuthContext } from "../context/AuthContext";
import { FaGamepad } from "react-icons/fa";

// Import Match interface from MatchPage
import { Match, PlayerStats } from "../pages/MatchPage";

// Import utilities
import {
	mapImages,
	getAvailableDataTypes,
	getPlayerStatsBySide,
	generateHeadToHeadData
} from '../utils/matchHelpers';
import { normalizeMatchData } from '../utils/matchDataNormalizer';
import { parseVetoLog, formatRoundReason } from '../utils/vetoParser';
import { useMappoolContext } from '../context/MappoolContext';

// Import components
import HeadToHeadView from './match/HeadToHeadView';
import MapSelector from './match/MapSelector';
import TeamStatsSection from './match/TeamStatsSection';
import RoundsVisualTimeline from './match/RoundsVisualTimeline';
import VetoSection from './match/VetoSection';
import HighlightsSection from './match/HighlightsSection';

// Import types from match.types.ts
import { Account, Team, PlayerMatchStats, MatchData, VetoStep, H2HDuel } from '../types/match.types';
import { MatchHighlights } from '../types/highlights.types';

interface RoundEnd {
	reason: string;
	teamWonID: string;
}

interface PlayedMatchViewProps {
	match: Match;
	matchData: MatchData[];
	team1Players: PlayerStats[];
	team2Players: PlayerStats[];
	highlightsData?: MatchHighlights | null;
	season1TeamLogos?: Record<string, string>;
}

// Side filter type
type SideFilter = "all" | "t" | "ct" | "h2h";

// Map images now imported from matchHelpers

// Helper functions now imported from matchHelpers

// Helper functions removed - now imported from matchHelpers


// Normalize match data function now imported from matchDataNormalizer

// VetoStep interface now imported from match.types.ts

const PlayedMatchView: React.FC<PlayedMatchViewProps> = ({
	match,
	matchData: rawMatchData,
	team1Players: existingTeam1Players,
	team2Players: existingTeam2Players,
	highlightsData,
	season1TeamLogos,
}) => {
	const { isLoggedinAdmin } = useContext(AuthContext);
	const { getMappool } = useMappoolContext();
	const [matches, setMatches] = useState<MatchData[]>([]);
	const [team1Players, setTeam1Players] = useState<PlayerMatchStats[]>([]);
	const [team2Players, setTeam2Players] = useState<PlayerMatchStats[]>([]);
	const [allMapsTeam1Players, setAllMapsTeam1Players] = useState<
		PlayerMatchStats[][]
	>([]);
	const [allMapsTeam2Players, setAllMapsTeam2Players] = useState<
		PlayerMatchStats[][]
	>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [selectedMapIndex, setSelectedMapIndex] = useState(0);
	const [selectedSide, setSelectedSide] = useState<SideFilter>("all");
	const [mapPool, setMapPool] = useState<string[]>([]);

	// Load mappool for the match's season
	useEffect(() => {
		const loadMappool = async () => {
			if (match && match.season) {
				try {
					const seasonMappool = await getMappool(match.season);
					setMapPool(seasonMappool);
				} catch (error) {
					console.error('Error loading mappool:', error);
					setMapPool([]);
				}
			}
		};
		loadMappool();
	}, [match, getMappool]);

	// Helper function to map scores to header team order
	const mapScoreToHeaderOrder = (matchData: MatchData) => {
		if (match.team1.teamId === matchData.team1.teamId) {
			return {
				headerTeam1Score: matchData.team1score,
				headerTeam2Score: matchData.team2score,
				headerTeam1Won: matchData.team1won,
			};
		} else {
			return {
				headerTeam1Score: matchData.team2score,
				headerTeam2Score: matchData.team1score,
				headerTeam1Won: !matchData.team1won,
			};
		}
	};

	// Helper function to map player stats to header team order
	const mapPlayersToHeaderOrder = (
		team1Players: PlayerMatchStats[],
		team2Players: PlayerMatchStats[]
	) => {

		// If no players, return as-is
		if (team1Players.length === 0 && team2Players.length === 0) {
			return {
				headerTeam1Players: team1Players,
				headerTeam2Players: team2Players,
			};
		}

		// Get a sample player from each team to check their team IDs
		const sampleTeam1Player = team1Players.length > 0 ? team1Players[0] : null;
		const sampleTeam2Player = team2Players.length > 0 ? team2Players[0] : null;

		if (sampleTeam1Player) {
		}

		if (sampleTeam2Player) {
		}

		// Direct comparison with header team IDs
		const team1MatchesHeader =
			sampleTeam1Player && sampleTeam1Player.team.teamId === match.team1.teamId;
		const team2MatchesHeader =
			sampleTeam2Player && sampleTeam2Player.team.teamId === match.team2.teamId;


		// If team1 players belong to header team1, keep the order
		if (team1MatchesHeader && team2MatchesHeader) {
			return {
				headerTeam1Players: team1Players,
				headerTeam2Players: team2Players,
			};
		}

		// If team1 players belong to header team2, swap the order
		if (
			sampleTeam1Player &&
			sampleTeam1Player.team.teamId === match.team2.teamId
		) {
			return {
				headerTeam1Players: team2Players,
				headerTeam2Players: team1Players,
			};
		}

		// Fallback: try to sort all players by header team IDs
		const allPlayers = [...team1Players, ...team2Players];
		const headerTeam1Players = allPlayers.filter(
			(player) => player.team.teamId === match.team1.teamId
		);
		const headerTeam2Players = allPlayers.filter(
			(player) => player.team.teamId === match.team2.teamId
		);


		return {
			headerTeam1Players,
			headerTeam2Players,
		};
	};

	useEffect(() => {
		processMatchData();
	}, [rawMatchData, match.challonge_id]);

	const processMatchData = async () => {
		try {
			setLoading(true);
			setError(null);


			if (
				rawMatchData &&
				Array.isArray(rawMatchData) &&
				rawMatchData.length > 0
			) {

				rawMatchData.forEach((match, index) => {
				});

				const normalizedMatches = normalizeMatchData(rawMatchData);

				normalizedMatches.forEach((match, index) => {
				});

				setMatches(normalizedMatches);

				const initialMapIndex = normalizedMatches.length > 1 ? -1 : 0;
				setSelectedMapIndex(initialMapIndex);
				await fetchAllMapStats(normalizedMatches, initialMapIndex);
			} else if (rawMatchData && !Array.isArray(rawMatchData)) {

				setMatches([rawMatchData]);
				setSelectedMapIndex(0);
				const { team1Stats, team2Stats } = await fetchPlayerStats(
					rawMatchData,
					0
				);
				const mappedStats = mapPlayersToHeaderOrder(team1Stats, team2Stats);
				setTeam1Players(mappedStats.headerTeam1Players);
				setTeam2Players(mappedStats.headerTeam2Players);
			} else {
				throw new Error("No match data available");
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred");
		} finally {
			setLoading(false);
		}
	};

	const fetchAllMapStats = async (
		matchDataArray: MatchData[],
		initialMapIndex: number = 0
	) => {
		const team1StatsAll: PlayerMatchStats[][] = [];
		const team2StatsAll: PlayerMatchStats[][] = [];

		for (let i = 0; i < matchDataArray.length; i++) {
			const { team1Stats, team2Stats } = await fetchPlayerStats(
				matchDataArray[i],
				i
			);
			team1StatsAll.push(team1Stats);
			team2StatsAll.push(team2Stats);
		}

		setAllMapsTeam1Players(team1StatsAll);
		setAllMapsTeam2Players(team2StatsAll);

		if (initialMapIndex === -1) {
			const aggregatedTeam1 = aggregatePlayerStats(team1StatsAll);
			const aggregatedTeam2 = aggregatePlayerStats(team2StatsAll);
			const mappedStats = mapPlayersToHeaderOrder(
				aggregatedTeam1,
				aggregatedTeam2
			);
			setTeam1Players(mappedStats.headerTeam1Players);
			setTeam2Players(mappedStats.headerTeam2Players);
		} else if (team1StatsAll.length > 0 && team2StatsAll.length > 0) {
			const mappedStats = mapPlayersToHeaderOrder(
				team1StatsAll[0],
				team2StatsAll[0]
			);
			setTeam1Players(mappedStats.headerTeam1Players);
			setTeam2Players(mappedStats.headerTeam2Players);
		}
	};

	const fetchPlayerStats = async (matchData: MatchData, mapIndex: number) => {
		try {

			const team1PlayerPromises = matchData.team1playerids.map(
				async (steamId: string) => {
					const response = await fetch(
						`https://api.rising.powerstats.dk/RisingStats/api/PlayerMatch/${matchData.gameid}/${steamId}`
					);
					if (!response.ok)
						throw new Error(`Failed to fetch player ${steamId}`);
					return response.json();
				}
			);

			const team2PlayerPromises = matchData.team2playerids.map(
				async (steamId: string) => {
					const response = await fetch(
						`https://api.rising.powerstats.dk/RisingStats/api/PlayerMatch/${matchData.gameid}/${steamId}`
					);
					if (!response.ok)
						throw new Error(`Failed to fetch player ${steamId}`);
					return response.json();
				}
			);

			const [team1Stats, team2Stats] = await Promise.all([
				Promise.all(team1PlayerPromises),
				Promise.all(team2PlayerPromises),
			]);

			team1Stats.forEach((player) => {
			});

			team2Stats.forEach((player) => {
			});

			return { team1Stats, team2Stats };
		} catch (err) {
			console.error("Error fetching player stats:", err);
			return { team1Stats: [], team2Stats: [] };
		}
	};

	// Enhanced aggregation function that handles side-specific stats
	const aggregatePlayerStats = (
		allMapsStats: PlayerMatchStats[][]
	): PlayerMatchStats[] => {
		if (allMapsStats.length === 0) return [];

		const playerStatsMap = new Map<string, PlayerMatchStats>();

		allMapsStats.forEach((mapStats) => {
			mapStats.forEach((playerStats) => {
				const steamId = playerStats.steamId;

				if (playerStatsMap.has(steamId)) {
					const existing = playerStatsMap.get(steamId)!;
					const aggregated: PlayerMatchStats = {
						...existing,
						gamesPlayed: existing.gamesPlayed + playerStats.gamesPlayed,

						// Overall stats
						kills: existing.kills + playerStats.kills,
						awpKills: existing.awpKills + playerStats.awpKills,
						hsKills: existing.hsKills + playerStats.hsKills,
						deaths: existing.deaths + playerStats.deaths,
						assists: existing.assists + playerStats.assists,
						dmg: existing.dmg + playerStats.dmg,
						fk: existing.fk + playerStats.fk,
						firstDeaths: existing.firstDeaths + playerStats.firstDeaths,
						roundsPlayed: existing.roundsPlayed + playerStats.roundsPlayed,
						pistolRoundsWon:
							existing.pistolRoundsWon + playerStats.pistolRoundsWon,
						pistolKills: existing.pistolKills + playerStats.pistolKills,
						entryKillsT: existing.entryKillsT + playerStats.entryKillsT,
						twoKillRounds: existing.twoKillRounds + playerStats.twoKillRounds,
						threeKillRounds:
							existing.threeKillRounds + playerStats.threeKillRounds,
						fourKillRounds:
							existing.fourKillRounds + playerStats.fourKillRounds,
						fiveKillRounds:
							existing.fiveKillRounds + playerStats.fiveKillRounds,

						// T-side stats
						tSideKills:
							(existing.tSideKills || 0) + (playerStats.tSideKills || 0),
						tSideAwpKills:
							(existing.tSideAwpKills || 0) + (playerStats.tSideAwpKills || 0),
						tSideHsKills:
							(existing.tSideHsKills || 0) + (playerStats.tSideHsKills || 0),
						tSideDeaths:
							(existing.tSideDeaths || 0) + (playerStats.tSideDeaths || 0),
						tSideAssists:
							(existing.tSideAssists || 0) + (playerStats.tSideAssists || 0),
						tSideDMG: (existing.tSideDMG || 0) + (playerStats.tSideDMG || 0),
						tSideFK: (existing.tSideFK || 0) + (playerStats.tSideFK || 0),
						tSideFirstDeaths:
							(existing.tSideFirstDeaths || 0) +
							(playerStats.tSideFirstDeaths || 0),
						tSideRoundsPlayed:
							(existing.tSideRoundsPlayed || 0) +
							(playerStats.tSideRoundsPlayed || 0),
						tSidePistolKills:
							(existing.tSidePistolKills || 0) +
							(playerStats.tSidePistolKills || 0),
						tSideEntryKills:
							(existing.tSideEntryKills || 0) +
							(playerStats.tSideEntryKills || 0),

						// CT-side stats
						ctSideKills:
							(existing.ctSideKills || 0) + (playerStats.ctSideKills || 0),
						ctSideAwpKills:
							(existing.ctSideAwpKills || 0) +
							(playerStats.ctSideAwpKills || 0),
						ctSideHsKills:
							(existing.ctSideHsKills || 0) + (playerStats.ctSideHsKills || 0),
						ctSideDeaths:
							(existing.ctSideDeaths || 0) + (playerStats.ctSideDeaths || 0),
						ctSideAssists:
							(existing.ctSideAssists || 0) + (playerStats.ctSideAssists || 0),
						ctSideDMG: (existing.ctSideDMG || 0) + (playerStats.ctSideDMG || 0),
						ctSideFK: (existing.ctSideFK || 0) + (playerStats.ctSideFK || 0),
						ctSideFirstDeaths:
							(existing.ctSideFirstDeaths || 0) +
							(playerStats.ctSideFirstDeaths || 0),
						ctSideRoundsPlayed:
							(existing.ctSideRoundsPlayed || 0) +
							(playerStats.ctSideRoundsPlayed || 0),
						ctSidePistolKills:
							(existing.ctSidePistolKills || 0) +
							(playerStats.ctSidePistolKills || 0),
					};

					// Calculate derived stats
					aggregated.kd =
						aggregated.deaths > 0
							? aggregated.kills / aggregated.deaths
							: aggregated.kills;
					aggregated.adr =
						aggregated.roundsPlayed > 0
							? aggregated.dmg / aggregated.roundsPlayed
							: 0;
					aggregated.hs =
						aggregated.kills > 0
							? (aggregated.hsKills / aggregated.kills) * 100
							: 0;

					// Calculate T-side derived stats
					if (aggregated.tSideDeaths! > 0) {
						aggregated.tSideKD =
							aggregated.tSideKills! / aggregated.tSideDeaths!;
					} else {
						aggregated.tSideKD = aggregated.tSideKills || 0;
					}

					if (aggregated.tSideRoundsPlayed! > 0) {
						aggregated.tSideADR =
							aggregated.tSideDMG! / aggregated.tSideRoundsPlayed!;
					}

					if (aggregated.tSideKills! > 0) {
						aggregated.tSideHS =
							(aggregated.tSideHsKills! / aggregated.tSideKills!) * 100;
					}

					// Calculate CT-side derived stats
					if (aggregated.ctSideDeaths! > 0) {
						aggregated.ctSideKD =
							aggregated.ctSideKills! / aggregated.ctSideDeaths!;
					} else {
						aggregated.ctSideKD = aggregated.ctSideKills || 0;
					}

					if (aggregated.ctSideRoundsPlayed! > 0) {
						aggregated.ctSideADR =
							aggregated.ctSideDMG! / aggregated.ctSideRoundsPlayed!;
					}

					if (aggregated.ctSideKills! > 0) {
						aggregated.ctSideHS =
							(aggregated.ctSideHsKills! / aggregated.ctSideKills!) * 100;
					}

					// For KAST and ratings, average them weighted by rounds played
					const totalRounds = existing.roundsPlayed + playerStats.roundsPlayed;
					if (totalRounds > 0) {
						aggregated.kast =
							(existing.kast * existing.roundsPlayed +
								playerStats.kast * playerStats.roundsPlayed) /
							totalRounds;
						aggregated.rating1 =
							(existing.rating1 * existing.roundsPlayed +
								playerStats.rating1 * playerStats.roundsPlayed) /
							totalRounds;
						aggregated.rating2 =
							(existing.rating2 * existing.roundsPlayed +
								playerStats.rating2 * playerStats.roundsPlayed) /
							totalRounds;
					}

					// For T-side KAST and ratings
					const totalTRounds =
						(existing.tSideRoundsPlayed || 0) +
						(playerStats.tSideRoundsPlayed || 0);
					if (totalTRounds > 0) {
						aggregated.tSideKAST =
							((existing.tSideKAST || 0) * (existing.tSideRoundsPlayed || 0) +
								(playerStats.tSideKAST || 0) *
									(playerStats.tSideRoundsPlayed || 0)) /
							totalTRounds;
						aggregated.tSideRating1 =
							((existing.tSideRating1 || 0) *
								(existing.tSideRoundsPlayed || 0) +
								(playerStats.tSideRating1 || 0) *
									(playerStats.tSideRoundsPlayed || 0)) /
							totalTRounds;
						aggregated.tSideRating2 =
							((existing.tSideRating2 || 0) *
								(existing.tSideRoundsPlayed || 0) +
								(playerStats.tSideRating2 || 0) *
									(playerStats.tSideRoundsPlayed || 0)) /
							totalTRounds;
					}

					// For CT-side KAST and ratings
					const totalCTRounds =
						(existing.ctSideRoundsPlayed || 0) +
						(playerStats.ctSideRoundsPlayed || 0);
					if (totalCTRounds > 0) {
						aggregated.ctSideKAST =
							((existing.ctSideKAST || 0) * (existing.ctSideRoundsPlayed || 0) +
								(playerStats.ctSideKAST || 0) *
									(playerStats.ctSideRoundsPlayed || 0)) /
							totalCTRounds;
						aggregated.ctSideRating1 =
							((existing.ctSideRating1 || 0) *
								(existing.ctSideRoundsPlayed || 0) +
								(playerStats.ctSideRating1 || 0) *
									(playerStats.ctSideRoundsPlayed || 0)) /
							totalCTRounds;
						aggregated.ctSideRating2 =
							((existing.ctSideRating2 || 0) *
								(existing.ctSideRoundsPlayed || 0) +
								(playerStats.ctSideRating2 || 0) *
									(playerStats.ctSideRoundsPlayed || 0)) /
							totalCTRounds;
					}

					// Opening duel percentages
					aggregated.openingDuelWinPercentage =
						(existing.openingDuelWinPercentage * existing.gamesPlayed +
							playerStats.openingDuelWinPercentage * playerStats.gamesPlayed) /
						aggregated.gamesPlayed;
					aggregated.openingDuelAttemptPercentage =
						(existing.openingDuelAttemptPercentage * existing.gamesPlayed +
							playerStats.openingDuelAttemptPercentage *
								playerStats.gamesPlayed) /
						aggregated.gamesPlayed;

					// Aggregate killsAgainstPlayers
					const aggregatedKillsAgainst = { ...existing.killsAgainstPlayers };
					Object.entries(playerStats.killsAgainstPlayers || {}).forEach(
						([victimId, kills]) => {
							aggregatedKillsAgainst[victimId] =
								(aggregatedKillsAgainst[victimId] || 0) + kills;
						}
					);
					aggregated.killsAgainstPlayers = aggregatedKillsAgainst;

					// Aggregate T-side killsAgainstPlayers
					const aggregatedTSideKillsAgainst = {
						...(existing.tSideKillsAgainstPlayers || {}),
					};
					Object.entries(playerStats.tSideKillsAgainstPlayers || {}).forEach(
						([victimId, kills]) => {
							aggregatedTSideKillsAgainst[victimId] =
								(aggregatedTSideKillsAgainst[victimId] || 0) + kills;
						}
					);
					aggregated.tSideKillsAgainstPlayers = aggregatedTSideKillsAgainst;

					// Aggregate CT-side killsAgainstPlayers
					const aggregatedCTSideKillsAgainst = {
						...(existing.ctSideKillsAgainstPlayers || {}),
					};
					Object.entries(playerStats.ctSideKillsAgainstPlayers || {}).forEach(
						([victimId, kills]) => {
							aggregatedCTSideKillsAgainst[victimId] =
								(aggregatedCTSideKillsAgainst[victimId] || 0) + kills;
						}
					);
					aggregated.ctSideKillsAgainstPlayers = aggregatedCTSideKillsAgainst;

					playerStatsMap.set(steamId, aggregated);
				} else {
					playerStatsMap.set(steamId, { ...playerStats });
				}
			});
		});

		return Array.from(playerStatsMap.values());
	};

	// Helper function to convert short team IDs to UUID format
	const shortIdToUuid = (shortId: string): string => {
		if (shortId.includes('-')) return shortId;
		const paddedId = shortId.padStart(12, '0');
		return `00000000-0000-0000-0000-${paddedId}`;
	};

	// Helper function to get team logo URL
	const getTeamLogoUrl = (team: Team) => {
		const placeholder = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

		if (!team || !team.teamId) return placeholder;

		// For season 1, check the cache first
		if (match.season === 1 && season1TeamLogos) {
			// Try direct lookup
			if (season1TeamLogos[team.teamId]) {
				return season1TeamLogos[team.teamId];
			}
			// Try converting short ID to UUID format
			const uuidId = shortIdToUuid(team.teamId);
			if (season1TeamLogos[uuidId]) {
				return season1TeamLogos[uuidId];
			}
			return placeholder;
		}

		return `${baseURL}/Team/${team.teamId}/logo`;
	};

	// Get map image
	const getMapImage = (map: string): string => {
		return mapImages[map] || "/images/maps/default.jpg";
	};

	// getMapPool now imported from vetoParser

	// parseVetoLog now imported from vetoParser


	// formatRoundReason now imported from vetoParser

	const getRoundsTimeline = (matchData: MatchData) => {
		if (!matchData) return [];

		return Object.entries(matchData.roundEnds).map(([roundNum, roundData]) => {
			const round = parseInt(roundNum);
			
			let winner: string;
			
			if (roundData.reason === "RoundDraw" || !roundData.teamWonID) {
				// Round draw is a CT win - need to determine which team is CT
				// Check if we're in first half (rounds 1-12) or second half (13+)
				const isFirstHalf = round <= 12;
				const startRound = isFirstHalf ? 1 : 13;
				const endRound = isFirstHalf ? 12 : 24;
				
				// Look for CT wins in the same half to determine which team is CT
				let ctTeam: string | null = null;
				
				for (let i = startRound; i <= endRound; i++) {
					const checkRound = matchData.roundEnds[i.toString()];
					if (checkRound && checkRound.teamWonID && 
						(checkRound.reason === "CT Win" || checkRound.reason === "Bomb Defused" || 
						 checkRound.reason === "CTsWin" || checkRound.reason === "BombDefused")) {
						// Found a CT win - this team is CT in this half
						if (checkRound.teamWonID === match.team1.teamId) {
							ctTeam = "team1";
							break;
						} else if (checkRound.teamWonID === match.team2.teamId) {
							ctTeam = "team2";
							break;
						} else if (checkRound.teamWonID === matchData.team1.teamId) {
							// Map to header team order
							ctTeam = match.team1.teamId === matchData.team1.teamId ? "team1" : "team2";
							break;
						} else if (checkRound.teamWonID === matchData.team2.teamId) {
							// Map to header team order
							ctTeam = match.team1.teamId === matchData.team2.teamId ? "team1" : "team2";
							break;
						}
					}
				}
				
				// If we couldn't find any CT wins, check for T wins to determine by elimination
				if (!ctTeam) {
					for (let i = startRound; i <= endRound; i++) {
						const checkRound = matchData.roundEnds[i.toString()];
						if (checkRound && checkRound.teamWonID && 
							(checkRound.reason === "T Win" || checkRound.reason === "Bomb Exploded" ||
							 checkRound.reason === "TerroristsWin" || checkRound.reason === "TargetBombed")) {
							// Found a T win - the OTHER team is CT in this half
							if (checkRound.teamWonID === match.team1.teamId) {
								ctTeam = "team2"; // team1 won as T, so team2 is CT
								break;
							} else if (checkRound.teamWonID === match.team2.teamId) {
								ctTeam = "team1"; // team2 won as T, so team1 is CT
								break;
							} else if (checkRound.teamWonID === matchData.team1.teamId) {
								// Map to header team order
								ctTeam = match.team1.teamId === matchData.team1.teamId ? "team2" : "team1";
								break;
							} else if (checkRound.teamWonID === matchData.team2.teamId) {
								// Map to header team order
								ctTeam = match.team1.teamId === matchData.team2.teamId ? "team2" : "team1";
								break;
							}
						}
					}
				}
				
				// Default to team1 if we still couldn't determine
				winner = ctTeam || "team1";
			} else {
				// Normal round - use the teamWonID
				winner = roundData.teamWonID === match.team1.teamId ? "team1" : 
						roundData.teamWonID === match.team2.teamId ? "team2" : 
						// Fallback: if team IDs don't match, try to use matchData order
						(roundData.teamWonID === matchData.team1.teamId ? "team1" : "team2");
			}
			
			return {
				round,
				winner,
				reason: formatRoundReason(roundData.reason),
			};
		});
	};

	const handleMapChange = async (index: number) => {
		setSelectedMapIndex(index);

		if (index === -1) {
			const aggregatedTeam1 = aggregatePlayerStats(allMapsTeam1Players);
			const aggregatedTeam2 = aggregatePlayerStats(allMapsTeam2Players);
			const mappedStats = mapPlayersToHeaderOrder(
				aggregatedTeam1,
				aggregatedTeam2
			);
			setTeam1Players(mappedStats.headerTeam1Players);
			setTeam2Players(mappedStats.headerTeam2Players);
		} else {
			if (allMapsTeam1Players[index] && allMapsTeam2Players[index]) {
				const mappedStats = mapPlayersToHeaderOrder(
					allMapsTeam1Players[index],
					allMapsTeam2Players[index]
				);
				setTeam1Players(mappedStats.headerTeam1Players);
				setTeam2Players(mappedStats.headerTeam2Players);
			}
		}

		// Check data availability and reset side filter if necessary
		setTimeout(() => {
			const currentTeam1 =
				index === -1
					? aggregatePlayerStats(allMapsTeam1Players)
					: allMapsTeam1Players[index] || [];
			const currentTeam2 =
				index === -1
					? aggregatePlayerStats(allMapsTeam2Players)
					: allMapsTeam2Players[index] || [];

			const { hasSideData, hasH2HData } = getAvailableDataTypes(
				currentTeam1,
				currentTeam2
			);

			// Reset to "all" if current selection is not available
			if ((selectedSide === "t" || selectedSide === "ct") && !hasSideData) {
				setSelectedSide("all");
			} else if (selectedSide === "h2h" && !hasH2HData) {
				setSelectedSide("all");
			}
		}, 0);
	};

	const handleSideChange = (side: SideFilter) => {
		const { hasSideData, hasH2HData } = getAvailableDataTypes(
			team1Players,
			team2Players
		);

		// Only allow side changes if data is available
		if ((side === "t" || side === "ct") && !hasSideData) {
			return; // Don't change if side data isn't available
		}
		if (side === "h2h" && !hasH2HData) {
			return; // Don't change if H2H data isn't available
		}

		setSelectedSide(side);
	};

	// Helper function to determine if a round was a T or CT win
	const getRoundWinSide = (reason: string): "ct-win" | "t-win" => {
		switch (reason) {
			case "CT Win":
			case "Bomb Defused":
				return "ct-win";
			case "T Win":
			case "Bomb Exploded":
				return "t-win";
			default:
				return "ct-win";
		}
	};

	// getRoundIcon moved to RoundsVisualTimeline component

	// HeadToHeadView component now imported from match/HeadToHeadView

	if (loading) {
		return (
			<div className="played-match-container">
				<div className="loading-state">
					<div className="loading-spinner"></div>
					<p>Loading match data...</p>
				</div>
			</div>
		);
	}

	if (error || matches.length === 0) {
		return (
			<div className="played-match-container">
				<div className="error-state">
					<h3>Error Loading Match</h3>
					<p>{error || "Match data not available"}</p>
				</div>
			</div>
		);
	}

	const currentMatch =
		selectedMapIndex >= 0 ? matches[selectedMapIndex] : matches[0];
	const roundsTimeline =
		selectedMapIndex >= 0 ? getRoundsTimeline(currentMatch) : [];
	const vetoSteps = parseVetoLog(
		currentMatch.vetoLog,
		currentMatch.team1,
		currentMatch.team2
	);

	// Apply side filtering to player data
	const filteredTeam1Players = team1Players.map((player) =>
		getPlayerStatsBySide(player, selectedSide)
	);
	const filteredTeam2Players = team2Players.map((player) =>
		getPlayerStatsBySide(player, selectedSide)
	);

	// Sort players by rating
	const sortedTeam1Players = [...filteredTeam1Players].sort(
		(a, b) => b.rating2 - a.rating2
	);
	const sortedTeam2Players = [...filteredTeam2Players].sort(
		(a, b) => b.rating2 - a.rating2
	);

	// Get available data types for current data
	const { hasSideData, hasH2HData } = getAvailableDataTypes(
		team1Players,
		team2Players
	);

	// Calculate overall series score mapped to header team order
	const seriesScore = matches.reduce(
		(acc, m) => {
			const mappedScore = mapScoreToHeaderOrder(m);
			if (mappedScore.headerTeam1Won) acc.headerTeam1++;
			else acc.headerTeam2++;
			return acc;
		},
		{ headerTeam1: 0, headerTeam2: 0 }
	);

	return (
		<>
			{/* Main Match Content - Normal Container */}
			<div className="played-match-container">
				{/* Series Header for BO3 */}
				{matches.length > 1 && (
					<MapSelector
						matches={matches}
						selectedMapIndex={selectedMapIndex}
						seriesScore={seriesScore}
						mapScoreToHeaderOrder={mapScoreToHeaderOrder}
						onMapChange={handleMapChange}
					/>
				)}

				{/* Unified Stats Header - Shows title for non-ALL views */}
				{(hasSideData || hasH2HData) && (
					<div className="stats-header-wrapper">
						<div className="h2h-header-row">
							{selectedSide !== "all" && (
								<h2 className="h2h-title">
									{selectedSide === "t" && "T SIDE"}
									{selectedSide === "ct" && "CT SIDE"}
									{selectedSide === "h2h" && "HEAD TO HEAD"}
								</h2>
							)}
							<div className="h2h-side-selector">
								<button
									className={`h2h-filter-btn ${selectedSide === "all" ? "active" : ""}`}
									onClick={() => handleSideChange("all")}
								>
									ALL
								</button>
								{hasSideData && (
									<>
										<button
											className={`h2h-filter-btn ${selectedSide === "t" ? "active" : ""}`}
											onClick={() => handleSideChange("t")}
										>
											T
										</button>
										<button
											className={`h2h-filter-btn ${selectedSide === "ct" ? "active" : ""}`}
											onClick={() => handleSideChange("ct")}
										>
											CT
										</button>
									</>
								)}
								<button
									className={`h2h-filter-btn ${selectedSide === "h2h" ? "active" : ""}`}
									onClick={() => handleSideChange("h2h")}
								>
									H2H
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Player Stats or H2H View */}
				{selectedSide === "h2h" ? (
					<HeadToHeadView
						players={[...team1Players, ...team2Players]}
						sideFilter={selectedSide}
						team1={match.team1}
						team2={match.team2}
						team1Logo={getTeamLogoUrl(match.team1)}
						team2Logo={getTeamLogoUrl(match.team2)}
					/>
				) : (
					<>
						{/* Team 1 */}
						<TeamStatsSection
							team={match.team1}
							players={sortedTeam1Players}
							isWinner={
								selectedMapIndex === -1
									? seriesScore.headerTeam1 > seriesScore.headerTeam2
									: mapScoreToHeaderOrder(currentMatch).headerTeam1Won
							}
							score={
								selectedMapIndex >= 0
									? mapScoreToHeaderOrder(currentMatch).headerTeam1Score
									: seriesScore.headerTeam1
							}
							selectedSide={selectedSide}
							teamLogoUrl={getTeamLogoUrl(match.team1)}
						/>

						{/* Visual Rounds Timeline - Only show for individual maps and not H2H */}
						{selectedMapIndex >= 0 && (
							<RoundsVisualTimeline roundsTimeline={roundsTimeline} />
						)}

						{/* Team 2 */}
						<TeamStatsSection
							team={match.team2}
							players={sortedTeam2Players}
							isWinner={
								selectedMapIndex === -1
									? seriesScore.headerTeam2 > seriesScore.headerTeam1
									: !mapScoreToHeaderOrder(currentMatch).headerTeam1Won
							}
							score={
								selectedMapIndex >= 0
									? mapScoreToHeaderOrder(currentMatch).headerTeam2Score
									: seriesScore.headerTeam2
							}
							selectedSide={selectedSide}
							teamLogoUrl={getTeamLogoUrl(match.team2)}
						/>
					</>
				)}
			</div>

			{/* Divider before veto section */}
			{vetoSteps.length > 0 && <hr className="series-divider" />}

			{/* Veto Process - Separate Wide Container - Show for both overall and individual maps */}
			{vetoSteps.length > 0 && (
				<VetoSection vetoSteps={vetoSteps} mapPool={mapPool} />
			)}

			{/* Divider before highlights section */}
			{highlightsData && highlightsData.highlightCount > 0 && <hr className="series-divider" />}

			{/* Highlights Section - Show match highlights if available */}
			{highlightsData && highlightsData.highlightCount > 0 && (
				<div className="played-match-container">
					<HighlightsSection
						highlightsData={highlightsData}
						matchId={match.challonge_id}
						team1Players={existingTeam1Players}
						team2Players={existingTeam2Players}
					/>
				</div>
			)}

			{/* Admin Veto Replace Button - Only visible to admins when veto data is invalid */}
			{isLoggedinAdmin && currentMatch && (
				(() => {
					// Check if veto data is invalid (e.g., just the text "veto" or empty/malformed)
					const vetoLog = currentMatch.vetoLog;
					const isInvalidVeto = !vetoLog || 
						vetoLog.trim().toLowerCase() === 'veto' || 
						vetoLog.trim() === '' ||
						vetoSteps.length === 0;
					
					if (isInvalidVeto) {
						return (
							<div className="veto-section">
								<div className="admin-veto-replace">
									<p className="veto-invalid-message">
										Veto data is missing or invalid for this match.
									</p>
									<a 
										href={`/match/${match.challonge_id}/veto`} 
										className="veto-button"
									>
										<FaGamepad />
										<span>Set Map Veto</span>
									</a>
								</div>
							</div>
						);
					}
					return null;
				})()
			)}
		</>
	);
};

export default PlayedMatchView;
